/**
 * Plays a static MP3 when an incoming dispatcher call is received.
 * URL: REACT_APP_INCOMING_CALL_MP3_URL (absolute URL to .mp3 in public CDN or /public).
 * Loops until {@link stopIncomingCallSound} is called (e.g. user dismisses the incoming-call toast).
 *
 * Browsers may block autoplay until the user has interacted with the page once.
 */

const DEDUPE_MS = 2500;

const recentByCallId = new Map<string, number>();
let lastGlobalPlay = 0;

let currentAudio: HTMLAudioElement | null = null;

export type IncomingCallSocketPayload = {
  type?: string;
  call_data?: { call_id?: string };
};

function pruneRecentMap(now: number) {
  if (recentByCallId.size > 50) {
    for (const [id, t] of recentByCallId) {
      if (now - t > DEDUPE_MS * 4) {
        recentByCallId.delete(id);
      }
    }
  }
}

export function playIncomingCallSound(
  payload: IncomingCallSocketPayload | null | undefined
): void {
  const url = process.env.REACT_APP_INCOMING_CALL_MP3_URL?.trim();
  if (!url) {
    return;
  }

  const now = Date.now();
  const callId = payload?.call_data?.call_id;

  if (callId) {
    const prev = recentByCallId.get(callId);
    if (prev && now - prev < DEDUPE_MS) {
      return;
    }
    recentByCallId.set(callId, now);
    pruneRecentMap(now);
  } else if (now - lastGlobalPlay < DEDUPE_MS) {
    return;
  } else {
    lastGlobalPlay = now;
  }

  try {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      currentAudio = null;
    }

    const audio = new Audio(url);
    audio.preload = 'auto';
    audio.loop = true;
    currentAudio = audio;

    void audio.play().catch(() => {
      // Autoplay blocked or load error — ignore silently
      console.log('Autoplay blocked or load error');
    });
  } catch {
    // Ignore
  }
}

/** Stops looping incoming-call audio (safe to call if nothing is playing). */
export function stopIncomingCallSound(): void {
  if (!currentAudio) {
    return;
  }
  try {
    currentAudio.pause();
    currentAudio.currentTime = 0;
    currentAudio.loop = false;
    currentAudio.src = '';
    currentAudio.load();
  } catch {
    // Ignore
  }
  currentAudio = null;
}
